<template>
  <transition name="appointment-readonly-panel">
    <div v-if="value" class="appointment-readonly-panel-shell">
      <div class="appointment-readonly-panel-backdrop" @click="closePanel" />

      <aside class="appointment-readonly-panel">
        <div class="appointment-readonly-panel__header">
          <div class="appointment-readonly-panel__title">
            <span class="appointment-readonly-panel__icon">
              <i class="fas fa-calendar-week" />
            </span>
            <span>Información de Cita</span>
          </div>

          <div class="appointment-readonly-panel__header-actions">
            <button
              type="button"
              class="appointment-readonly-panel__icon-button appointment-readonly-panel__icon-button--danger"
              @click="closePanel"
            >
              <i class="fal fa-times-circle" />
            </button>
          </div>
        </div>

        <div class="appointment-readonly-panel__body">
          <div v-if="loading" class="appointment-readonly-panel__loading">
            <custom-result-not-found text="Cargando información..." />
          </div>

          <template v-else-if="appointment">
            <custom-tab
              class="appointment-readonly-panel__tabs"
              nav-class="appointment-readonly-panel__tabs-nav"
              style="--tab-active-color: #ff7058; --tab-font-size: 16px;"
            >
              <custom-tab-item title="Información" class="mt-3">
                <div class="appointment-readonly-panel__dependent mb-2">
                  <span class="appointment-readonly-panel__dependent-label">
                    <i class="fas fa-user-circle color-orange mr-1" />
                    Dependiente:
                  </span>
                  <span>{{ dependentLabel }}</span>
                </div>

                <div class="appointment-readonly-panel__summary">
                  <span>Citaciones Totales</span>
                  <span>{{ appointment.citationNumber || 0 }}/5</span>
                </div>

                <div class="appointment-readonly-panel__checks-row">
                  <readonly-check
                    label="Padres"
                    :checked="appointment.includeParents"
                  />
                  <readonly-check
                    label="Estudiante"
                    :checked="appointment.includeStudent"
                  />
                </div>

                <div
                  class="appointment-readonly-panel__grid appointment-readonly-panel__grid--three"
                >
                  <readonly-field label="Fecha:" :value="formattedDate" />
                  <readonly-field label="Desde:" :value="formattedStartTime" />
                  <readonly-field label="Hasta:" :value="formattedEndTime" />
                </div>

                <readonly-field label="Lugar:" :value="appointment.location" />

                <div
                  class="appointment-readonly-panel__section appointment-readonly-panel__section--divided"
                >
                  <span class="appointment-readonly-panel__label">
                    Propósito:
                  </span>
                  <div class="appointment-readonly-panel__chips">
                    <span
                      v-for="purpose in purposeChips"
                      :key="purpose.key"
                      class="appointment-readonly-panel__chip"
                    >
                      {{ purpose.text }}
                    </span>
                    <span v-if="!purposeChips.length">-</span>
                  </div>
                </div>

                <div
                  v-if="
                    hasEvaluationDiscussionPurpose &&
                      appointmentEvaluations.length
                  "
                  class="appointment-readonly-panel__section appointment-readonly-panel__section--divided"
                >
                  <span class="appointment-readonly-panel__label">
                    Evaluacion(es) Seleccionada(s):
                  </span>
                  <div class="appointment-readonly-panel__evaluation-list">
                    <div
                      v-for="evaluation in appointmentEvaluations"
                      :key="evaluation.key"
                      class="appointment-readonly-panel__evaluation-row"
                    >
                      <div>
                        <span class="font-weight-bold">
                          Nombre de Evaluación:
                        </span>
                        {{ evaluation.name }}
                      </div>
                      <div>
                        <span class="font-weight-bold">
                          Fecha de Evaluación:
                        </span>
                        {{ evaluation.date }}
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  class="appointment-readonly-panel__section appointment-readonly-panel__numbers"
                >
                  <div>
                    <span class="appointment-readonly-panel__label">
                      Número de Citación:
                    </span>
                    <span class="appointment-readonly-panel__citation-badge">
                      <i class="fas fa-calendar-alt mr-1" />
                      {{ citationLabel }}
                    </span>
                  </div>
                  <div>
                    <span class="appointment-readonly-panel__label">
                      Número de Cita:
                    </span>
                    <span class="appointment-readonly-panel__number-value">
                      {{ appointment.compuAppointmentNumber || '-' }}
                    </span>
                  </div>
                </div>

                <div class="appointment-readonly-panel__section-title">
                  <span class="appointment-readonly-panel__section-icon">
                    <i class="fas fa-user" />
                  </span>
                  <span>Información Participantes</span>
                </div>

                <div class="appointment-readonly-panel__checks-column">
                  <readonly-check
                    v-for="participant in regularParticipantOptions"
                    :key="participant.id"
                    :label="participant.name"
                    :checked="isParticipantSelected(participant)"
                  />
                  <readonly-check
                    v-if="otherParticipantOption"
                    label="Otro:"
                    :checked="otherParticipants.length > 0"
                  />
                </div>

                <div
                  v-if="otherParticipants.length"
                  class="appointment-readonly-panel__other-list"
                >
                  <div
                    v-for="participant in otherParticipants"
                    :key="participant.id || participant.name"
                    class="appointment-readonly-panel__other-row"
                  >
                    {{ participant.name }}
                  </div>
                </div>

                <div class="appointment-readonly-panel__section-title">
                  <span class="appointment-readonly-panel__section-icon">
                    <i class="fas fa-user" />
                  </span>
                  <span>Funcionario</span>
                </div>

                <div
                  class="appointment-readonly-panel__grid appointment-readonly-panel__official-grid"
                >
                  <readonly-field label="Nombre:" :value="officialFullName" />
                  <readonly-field
                    label="Correo Electrónico:"
                    :value="appointment.officialEmail"
                  />
                  <readonly-field
                    label="Teléfono:"
                    :value="appointment.officialPhone"
                  />
                </div>

                <div class="appointment-readonly-panel__section">
                  <span class="appointment-readonly-panel__label">
                    Se notifica a los Padres a través de:
                  </span>
                  <div class="appointment-readonly-panel__checks-column">
                    <readonly-check
                      label="Personal"
                      :checked="appointment.notifyPersonal"
                    />
                    <readonly-check
                      label="Via Teléfono"
                      :checked="appointment.notifyPhone"
                    />
                    <readonly-check
                      label="Correo Electrónico"
                      :checked="appointment.notifyEmail"
                    />
                    <readonly-check
                      label="Otro"
                      :checked="appointment.notifyOther"
                    />
                  </div>
                  <readonly-field
                    v-if="appointment.notifyOther"
                    label="Detalle:"
                    :value="appointment.otherNotificationDescription"
                  />
                </div>

                <div class="appointment-readonly-panel__section">
                  <div class="appointment-readonly-panel__sign-plan">
                    <sign-plan
                      :is-electronic-signature="
                        appointment.isElectronicSignature
                      "
                      disabled
                      question-label="¿Cuál será el mecanismo que se estará utilizando para firmar la Citación?"
                      readonly-panel
                    />
                  </div>
                </div>

                <div
                  v-if="appointment.isElectronicSignature"
                  class="appointment-readonly-panel__signature-card"
                >
                  <div class="appointment-readonly-panel__signature-grid">
                    <readonly-field
                      class="appointment-readonly-panel__signature-name"
                      label="Nombre:"
                      :value="signatureDocumentName"
                    />
                    <readonly-field
                      label="Fecha Última Modificación:"
                      :value="signatureLastModifiedDate"
                    />
                    <div>
                      <span class="appointment-readonly-panel__label">
                        Estatus de Firma:
                      </span>
                      <span class="appointment-readonly-panel__status">
                        <span
                          class="appointment-readonly-panel__status-dot"
                          :style="signatureStatusDotStyle"
                        />
                        {{ signatureStatusName }}
                      </span>
                    </div>
                    <action-button
                      class="appointment-readonly-panel__configure-button"
                      :i="signatureConfigurationButtonIcon"
                      :text="signatureConfigurationButtonText"
                      variant="outline-success"
                      @click="handleConfigureSignatures"
                    />
                  </div>
                  <warning-box
                    v-if="shouldShowSignatureWarning"
                    class="appointment-readonly-panel__warning-box mx-0"
                    title="Esta Coordinación de cita se encuentra en proceso de firma,"
                  >
                    para proceder con el cambio se requiere Cancelar Proceso
                    Firma dentro de la pantalla de configuración.
                  </warning-box>
                  <Error
                    v-if="hasCancellationReason"
                    variant="pl-3"
                    :message="
                      appointment.digitalSignatureDocumentCancellationReason
                    "
                  />
                </div>

                <div
                  v-if="appointment.isElectronicSignature === false"
                  class="appointment-readonly-panel__manuscript-section"
                >
                  <div
                    class="appointment-readonly-panel__section-title mt-3 mb-2"
                  >
                    <span class="appointment-readonly-panel__section-icon">
                      <i class="fas fa-file-signature" />
                    </span>
                    <span>Documento de Firma Manuscrita</span>
                  </div>
                  <div class="appointment-readonly-panel__signature-card">
                    <readonly-field
                      label="Documento:"
                      :value="manuscriptDocumentName"
                    />
                    <action-button
                      class="appointment-readonly-panel__download-button mt-2"
                      i="fas fa-file-download text-white mr-n1 pr-2"
                      :text="manuscriptDownloadButtonText"
                      variant="success"
                      @click="downloadManuscriptDocument"
                    />
                  </div>
                </div>

                <div
                  v-if="appointment.compuMinuteId"
                  class="appointment-readonly-panel__section"
                >
                  <div
                    class="appointment-readonly-panel__section-title mt-3 mb-2"
                  >
                    <span class="appointment-readonly-panel__section-icon">
                      <i class="fas fa-file-alt" />
                    </span>
                    <span>Minuta Asociada</span>
                  </div>
                  <div class="appointment-readonly-panel__signature-card">
                    <readonly-field
                      label="Minuta:"
                      :value="appointment.compuMinuteTextSelection"
                    />
                  </div>
                </div>

                <div
                  v-if="
                    canCoordinateAppointment ||
                      canConfirmAppointment ||
                      canRegisterCelebration ||
                      canRecoordinateAppointment
                  "
                  class="appointment-readonly-panel__footer-actions mt-3"
                >
                  <action-button
                    v-if="canCoordinateAppointment"
                    class="appointment-readonly-panel__footer-button"
                    i="fas fa-calendar-check text-green mr-n1 pr-2"
                    text="Coordinar"
                    variant="outline-success"
                    @click="handleCoordinateAppointment"
                  />
                  <action-button
                    v-if="canConfirmAppointment"
                    class="appointment-readonly-panel__footer-button"
                    i="fa fa-check-circle text-green mr-n1 pr-2"
                    text="Confirmar Cita"
                    variant="outline-success"
                    @click="handleConfirmAppointment"
                  />
                  <action-button
                    v-if="canRegisterCelebration"
                    class="appointment-readonly-panel__footer-button"
                    i="fa fa-check-circle text-green mr-n1 pr-2"
                    text="Registrar celebración"
                    variant="outline-success"
                    @click="handleRegisterCelebration"
                  />
                  <action-button
                    v-if="canRecoordinateAppointment"
                    class="appointment-readonly-panel__recoordinate-button"
                    i="fa fa-check-circle text-green mr-n1 pr-2"
                    text="Recoordinar Cita"
                    variant="outline-success"
                    @click="handleRecoordinate"
                  />
                </div>
              </custom-tab-item>

              <custom-tab-item title="Historial de Citas" class="mt-3">
                <div
                  v-if="!history.length"
                  class="appointment-readonly-panel__empty-history"
                >
                  <i class="far fa-folder-open" />
                  <span>No existen citas previas para este estudiante.</span>
                </div>
                <div v-else class="appointment-readonly-panel__history-list">
                  <div
                    v-for="(historyItem, index) in history"
                    :key="
                      historyItem.id || historyItem.compuAppointmentId || index
                    "
                    class="appointment-readonly-panel__history-card"
                  >
                    <div
                      class="appointment-readonly-panel__history-card-header"
                    >
                      <span class="appointment-readonly-panel__history-badge">
                        <i class="fas fa-calendar-alt mr-1" />
                        {{ getCitationLabel(historyItem.citationNumber) }}
                      </span>
                    </div>
                    <div class="appointment-readonly-panel__history-grid">
                      <readonly-field
                        label="Fecha de Cita:"
                        :value="formatDisplayDate(historyItem.appointmentDate)"
                      />
                      <readonly-field
                        label="Hora Desde:"
                        :value="formatTime(historyItem.startTime)"
                      />
                      <readonly-field
                        label="Hora Hasta:"
                        :value="formatTime(historyItem.endTime)"
                      />
                      <readonly-field
                        class="appointment-readonly-panel__history-full-row"
                        label="Lugar:"
                        :value="historyItem.location"
                      />
                      <readonly-field
                        class="appointment-readonly-panel__history-full-row"
                        label="Funcionario:"
                        :value="historyItem.officialFullName"
                      />
                      <div class="appointment-readonly-panel__history-status-row">
                        <div class="appointment-readonly-panel__field">
                          <span class="appointment-readonly-panel__label">
                            Estatus:
                          </span>
                          <span class="appointment-readonly-panel__status">
                            <span
                              class="appointment-readonly-panel__status-dot"
                              :style="{
                                backgroundColor: getStatusDotColor(historyItem),
                              }"
                            />
                            {{ historyItem.statusName || '-' }}
                          </span>
                        </div>
                        <readonly-field
                          label="Respuesta de Cita:"
                          :value="historyItem.attendanceResponseName || '-'"
                        />
                      </div>
                      <readonly-field
                        class="appointment-readonly-panel__history-full-row"
                        label="Minuta:"
                        :value="historyItem.compuMinuteTextSelection || '-'"
                      />
                    </div>
                  </div>
                </div>
              </custom-tab-item>
            </custom-tab>
          </template>
        </div>
      </aside>
    </div>
  </transition>
</template>

<script>
import moment from 'moment';
import ActionButton from 'ActionButton';
import CustomResultNotFound from 'custom-result-not-found';
import mixAlert from '@/mixin/alert.mixin';
import calendarAppointmentDisplayMixin from './mixins/calendarAppointmentDisplayMixin';
import compuAppointmentService from '@/modules/compu-appointment/api';
import SignatureHubTypes from '@/utils/constants/signatureHubTypes';
import compuAppointmentStatus from '@/utils/constants/compuAppointmentStatus';
import DigitalSignatureDocumentStatus from '@/utils/constants/digitalSignatureDocumentStatus';
import { getSignatureHubInfo as _getSignatureHubInfo } from '@/services/signatureHub/api';
import { SignPlan } from '@/components/sign-plan';
import DocumentsService from '@/services/documents';
import reportService from '@/services/report';
import { CustomTab, CustomTabItem } from 'custom-tabs';
import WarningBox from '@/components/pei/service/components/ServiceWarningBox';
import Error from '@/components/common/Error';

const _OtherAppointmentParticipantTypeId = 6;
const _RecoordinableAppointmentStatusIds = [2, 3, 5];
const _ConfirmedAppointmentStatusId = 3;
const _NoResponseAppointmentStatusId = 9;
const _RegisterCelebrationAppointmentStatusIds = [
  _ConfirmedAppointmentStatusId,
  _NoResponseAppointmentStatusId,
];

const normalizeText = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const ReadonlyCheck = {
  name: 'ReadonlyCheck',
  functional: true,
  props: {
    label: { type: String, default: '' },
    checked: { type: Boolean, default: false },
  },
  render(h, { props }) {
    return h('div', { class: 'appointment-readonly-panel__check' }, [
      h(
        'span',
        {
          class: [
            'appointment-readonly-panel__check-box',
            {
              'appointment-readonly-panel__check-box--checked': props.checked,
            },
          ],
        },
        props.checked ? [h('i', { class: 'fas fa-check' })] : []
      ),
      h('span', props.label),
    ]);
  },
};

const ReadonlyField = {
  name: 'ReadonlyField',
  functional: true,
  props: {
    label: { type: String, default: '' },
    value: { type: [String, Number], default: '' },
  },
  render(h, { props, data }) {
    return h(
      'div',
      {
        class: [
          'appointment-readonly-panel__field',
          data.staticClass,
          data.class,
        ],
      },
      [
        h('span', { class: 'appointment-readonly-panel__label' }, props.label),
        h(
          'span',
          { class: 'appointment-readonly-panel__value' },
          props.value || '-'
        ),
      ]
    );
  },
};

export default {
  name: 'AppointmentReadOnlyPanel',
  mixins: [mixAlert, calendarAppointmentDisplayMixin],
  components: {
    ActionButton,
    CustomResultNotFound,
    ReadonlyCheck,
    ReadonlyField,
    SignPlan,
    CustomTab,
    CustomTabItem,
    WarningBox,
    Error,
  },
  props: {
    value: { type: Boolean, default: false },
    appointmentId: { type: Number, default: null },
    selectedAppointmentPurposes: { type: Array, default: () => [] },
  },
  data() {
    return {
      appointment: null,
      participantOptions: [],
      signatureHubInfo: null,
      history: [],
      loading: false,
    };
  },
  computed: {
    dependentLabel() {
      if (!this.appointment) {
        return '-';
      }

      return `${this.appointment.studentSieId || '-'} - ${this.appointment
        .studentFullName || '-'}`;
    },
    formattedDate() {
      return this.formatDisplayDate(this.appointment?.appointmentDate);
    },
    formattedStartTime() {
      return this.formatTime(this.appointment?.startTime);
    },
    formattedEndTime() {
      return this.formatTime(this.appointment?.endTime);
    },
    citationLabel() {
      return this.getCitationLabel(this.appointment?.citationNumber);
    },
    purposeChips() {
      return this.visiblePurposes.map((purpose, index) => ({
        key: `${purpose.id || 'purpose'}-${index}`,
        text: purpose.otherDescription
          ? `${purpose.name}: ${purpose.otherDescription}`
          : purpose.name,
      }));
    },
    appointmentEvaluations() {
      return (this.appointment?.evaluations || []).map((evaluation, index) => ({
        key:
          evaluation.id ||
          evaluation.studentEvaluationId ||
          `appointment-evaluation-${index}`,
        name: evaluation.name || '-',
        date: this.formatDisplayDate(evaluation.evaluationDate),
      }));
    },
    visiblePurposes() {
      if (this.selectedAppointmentPurposes.length) {
        return this.selectedAppointmentPurposes;
      }

      return this.appointment?.purposes || [];
    },
    hasEvaluationDiscussionPurpose() {
      return this.visiblePurposes.some((purpose) => {
        const purposeName = normalizeText(
          purpose.name || purpose.text || purpose.description
        );

        return (
          purposeName.includes('evaluacion') &&
          (purposeName.includes('discutir') ||
            purposeName.includes('discusion'))
        );
      });
    },
    regularParticipantOptions() {
      return this.participantOptions.filter(
        (participant) => !participant.isOther
      );
    },
    otherParticipantOption() {
      return this.participantOptions.find((participant) => participant.isOther);
    },
    selectedParticipants() {
      return this.appointment?.participants || [];
    },
    otherParticipants() {
      return this.selectedParticipants
        .filter(
          (participant) =>
            participant.isOther ||
            Number(participant.participantTypeId) ===
              _OtherAppointmentParticipantTypeId
        )
        .map((participant, index) => ({
          id: participant.id || `other-${index}`,
          name:
            participant.otherDescription ||
            participant.OtherDescription ||
            participant.description ||
            participant.name ||
            '-',
        }));
    },
    officialFullName() {
      if (!this.appointment) {
        return '-';
      }

      return [
        this.appointment.officialFirstName,
        this.appointment.officialInitial,
        this.appointment.officialLastName,
        this.appointment.officialSecondLastName,
      ]
        .filter(Boolean)
        .join(' ');
    },
    signatureDocumentInfo() {
      return this.signatureHubInfo?.documentInfo || {};
    },
    signatureDocumentName() {
      return (
        this.signatureDocumentInfo.documentName ||
        this.signatureDocumentInfo.documentFilename ||
        this.signatureDocumentInfo.name ||
        `Confirmación de Cita - ${this.dependentLabel}`
      );
    },
    signatureLastModifiedDate() {
      return this.formatDisplayDate(
        this.appointment?.updatedDate ||
          this.appointment?.UpdatedDate ||
          this.signatureDocumentInfo.lastModifiedDate ||
          this.signatureDocumentInfo.updatedDate ||
          this.signatureDocumentInfo.modifiedDate
      );
    },
    signatureStatusId() {
      const statusId =
        this.appointment?.digitalSignatureDocumentStatusId ||
        this.appointment?.DigitalSignatureDocumentStatusId ||
        this.signatureDocumentInfo.documentStatusId;

      return statusId == null ? null : Number(statusId);
    },
    signatureStatusName() {
      const statusNames = {
        [DigitalSignatureDocumentStatus.Draft]: 'Borrador',
        [DigitalSignatureDocumentStatus.SignatureSetupProcess]:
          'Proceso de Configuración',
        [DigitalSignatureDocumentStatus.SignaturePending]: 'Pendiente de Firma',
        [DigitalSignatureDocumentStatus.Cancelled]: 'Cancelado',
        [DigitalSignatureDocumentStatus.Signed]: 'Firmado',
        [DigitalSignatureDocumentStatus.RenderedIneffective]:
          'Documento sin Efecto',
        [DigitalSignatureDocumentStatus.Rejected]: 'Rechazado',
      };

      if (statusNames[this.signatureStatusId]) {
        return statusNames[this.signatureStatusId];
      }

      return (
        this.signatureDocumentInfo.documentStatusName ||
        this.signatureDocumentInfo.statusName ||
        'Pendiente de Firma'
      );
    },
    signatureStatusDotStyle() {
      const statusColors = {
        [DigitalSignatureDocumentStatus.Draft]: '#949494',
        [DigitalSignatureDocumentStatus.SignatureSetupProcess]: '#8277ff',
        [DigitalSignatureDocumentStatus.SignaturePending]: '#8277ff',
        [DigitalSignatureDocumentStatus.Cancelled]: '#d9534f',
        [DigitalSignatureDocumentStatus.Signed]: '#8ec700',
        [DigitalSignatureDocumentStatus.RenderedIneffective]: '#949494',
        [DigitalSignatureDocumentStatus.Rejected]: '#d9534f',
      };

      return {
        backgroundColor:
          statusColors[this.signatureStatusId] ||
          statusColors[DigitalSignatureDocumentStatus.SignaturePending],
      };
    },
    isSignatureSigned() {
      return this.signatureStatusId === DigitalSignatureDocumentStatus.Signed;
    },
    isSignatureInProcess() {
      return [
        DigitalSignatureDocumentStatus.SignatureSetupProcess,
        DigitalSignatureDocumentStatus.SignaturePending,
      ].includes(this.signatureStatusId);
    },
    isSignaturePending() {
      return this.signatureStatusName === 'Pendiente de Firma';
    },
    shouldShowSignatureWarning() {
      return (
        this.appointment?.isElectronicSignature &&
        !this.isSignatureSigned &&
        (this.isSignatureInProcess || this.isSignaturePending)
      );
    },
    canRecoordinateAppointment() {
      const statusId =
        this.appointment?.statusId ??
        this.appointment?.compuAppointmentStatusId ??
        this.appointment?.appointmentStatusId;

      return _RecoordinableAppointmentStatusIds.includes(Number(statusId));
    },
    canRegisterCelebration() {
      const statusId =
        this.appointment?.statusId ??
        this.appointment?.compuAppointmentStatusId ??
        this.appointment?.appointmentStatusId;

      if (
        !_RegisterCelebrationAppointmentStatusIds.includes(Number(statusId))
      ) {
        return false;
      }

      if (!this.appointment?.appointmentDate) {
        return false;
      }

      return moment(this.appointment.appointmentDate)
        .startOf('day')
        .isBefore(moment().startOf('day'));
    },
    canCoordinateAppointment() {
      const statusId =
        this.appointment?.statusId ??
        this.appointment?.compuAppointmentStatusId ??
        this.appointment?.appointmentStatusId;

      return (
        Number(statusId) === compuAppointmentStatus.PendienteDeFirma &&
        this.appointment?.isElectronicSignature === false
      );
    },
    canConfirmAppointment() {
      const statusId =
        this.appointment?.statusId ??
        this.appointment?.compuAppointmentStatusId ??
        this.appointment?.appointmentStatusId;

      return (
        Number(statusId) === compuAppointmentStatus.Coordinada &&
        this.appointment?.isElectronicSignature === false
      );
    },
    signatureConfigurationButtonText() {
      return this.isSignatureSigned
        ? 'Seguimiento Firma'
        : 'Continuar Configuración';
    },
    signatureConfigurationButtonIcon() {
      return this.isSignatureSigned
        ? 'fas fa-eye text-green mr-n1 pr-2'
        : 'fas fa-pen text-green mr-n1 pr-2';
    },
    manuscriptDocument() {
      return (
        this.appointment?.manuscriptDocument ||
        this.appointment?.manuscriptSignedDocument ||
        this.appointment?.citationDocument ||
        this.appointment?.signedDocument ||
        null
      );
    },
    manuscriptDocumentId() {
      return (
        this.manuscriptDocument?.documentId ||
        this.manuscriptDocument?.id ||
        this.appointment?.documentId ||
        this.appointment?.manuscriptDocumentId ||
        this.appointment?.manuscriptSignedDocumentId ||
        this.appointment?.citationDocumentId ||
        this.appointment?.signedDocumentId ||
        null
      );
    },
    hasManuscriptDocument() {
      return Boolean(this.manuscriptDocumentId);
    },
    hasCancellationReason() {
      return Boolean(
        this.appointment?.digitalSignatureDocumentCancellationReason
      );
    },
    manuscriptDocumentName() {
      return (
        this.manuscriptDocument?.documentName ||
        this.manuscriptDocument?.documentFilename ||
        this.manuscriptDocument?.filename ||
        this.manuscriptDocument?.fileName ||
        this.manuscriptDocument?.name ||
        this.appointment?.documentName ||
        this.appointment?.documentFilename ||
        this.appointment?.filename ||
        this.appointment?.fileName ||
        (this.manuscriptDocumentId
          ? 'Documento adjunto firmado'
          : 'No hay documento adjunto disponible.')
      );
    },
    manuscriptDownloadButtonText() {
      return this.hasManuscriptDocument ? 'Descargar' : 'Descargar Citación';
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(isOpen) {
        if (isOpen) {
          this.resetPanel();
          if (this.appointmentId) {
            this.loadAppointment(this.appointmentId);
          }
        }
      },
    },
    appointmentId(id, previousId) {
      if (this.value && id && previousId && id !== previousId) {
        this.resetPanel();
        this.loadAppointment(id);
      }
    },
  },
  methods: {
    closePanel() {
      this.$emit('input', false);
    },
    resetPanel() {
      this.appointment = null;
      this.participantOptions = [];
      this.signatureHubInfo = null;
      this.history = [];
    },
    async loadAppointment(id) {
      try {
        this.loading = true;
        const { data } = await compuAppointmentService.getAppointmentInfo(id);
        this.appointment = data;

        await Promise.all([
          this.fetchParticipantOptions(),
          this.fetchSignatureInfo(),
          this.fetchHistory(),
        ]);
      } catch {
        this.ShowToast(
          'Error',
          'No se pudo cargar la informacion de la cita.',
          'error'
        );
      } finally {
        this.loading = false;
      }
    },
    async fetchParticipantOptions() {
      if (
        !this.appointment?.studentSieId ||
        !this.appointment?.citationNumber
      ) {
        this.participantOptions = [];
        return;
      }

      try {
        const { data } = await compuAppointmentService.fetchParticipantOptions(
          this.appointment.studentSieId,
          this.appointment.citationNumber
        );
        this.participantOptions = Array.isArray(data) ? data : data?.data || [];
      } catch {
        this.participantOptions = [];
      }
    },
    async fetchSignatureInfo() {
      if (!this.appointment?.isElectronicSignature || !this.appointment?.id) {
        return;
      }

      try {
        const { data } = await _getSignatureHubInfo(
          SignatureHubTypes.COMPU_APPOINTMENT,
          this.appointment.id
        );
        this.signatureHubInfo = data;
      } catch {
        this.signatureHubInfo = null;
      }
    },
    async fetchHistory() {
      if (!this.appointment?.studentSieId) {
        this.history = [];
        return;
      }

      try {
        const { data } = await compuAppointmentService.fetchStudentHistory(
          this.appointment.studentSieId
        );
        this.history = Array.isArray(data) ? data : data?.data || [];
      } catch {
        this.history = [];
      }
    },
    isParticipantSelected(participantOption) {
      return this.selectedParticipants.some(
        (participant) =>
          Number(participant.participantTypeId) === Number(participantOption.id)
      );
    },
    formatDisplayDate(value) {
      if (!value) {
        return '-';
      }

      return moment(value)
        .locale('es')
        .format('DD/MMM./YYYY')
        .replace('..', '.');
    },
    formatTime(value) {
      if (!value) {
        return '-';
      }

      const time = `${value}`.split('.')[0];
      const parts = time.split(':');

      if (parts.length < 2) {
        return value;
      }

      const hour = Number(parts[0]);
      const minute = parts[1];
      const suffix = hour >= 12 ? 'PM' : 'AM';
      const normalizedHour = hour % 12 || 12;

      return `${normalizedHour}:${minute}${suffix}`;
    },
    getCitationLabel(citationNumber) {
      if (!citationNumber) {
        return 'Cita';
      }

      const suffixes = {
        1: 'ra',
        2: 'da',
        3: 'ra',
        4: 'ta',
        5: 'ta',
      };

      return `${citationNumber}${suffixes[Number(citationNumber)] ||
        'ta'} Cita`;
    },
    handleConfigureSignatures() {
      if (!this.appointment?.id) {
        return;
      }

      this.$emit('open-signature-hub', {
        appointmentId: this.appointment.id,
        keepReadonlyQuery: true,
      });
    },
    handleRecoordinate() {
      this.$emit('recoordinate', this.appointment);
    },
    handleRegisterCelebration() {
      this.$emit('register-celebration', this.appointment);
    },
    handleCoordinateAppointment() {
      if (this.canCoordinateAppointment) {
        this.$emit('coordinate-appointment', this.appointment);
      }
    },
    handleConfirmAppointment() {
      this.$emit('confirm-appointment', this.appointment);
    },
    async downloadManuscriptDocument() {
      if (!this.manuscriptDocumentId) {
        if (
          !this.appointment?.studentSieId ||
          !this.appointment?.citationNumber
        ) {
          this.ShowToast(
            'Alerta',
            'No fue posible identificar la citación para descargar.',
            'error'
          );
          return;
        }

        try {
          await reportService.getCompuMeetingCoordination(
            this.appointment.studentSieId,
            this.appointment.citationNumber
          );
        } catch (error) {
          this.ShowToast(
            'Alerta',
            error?.response?.data?.message ||
              'No fue posible descargar la citación.',
            'error'
          );
        }

        return;
      }

      try {
        await DocumentsService.downloadFromServer(this.manuscriptDocumentId);
      } catch {
        this.ShowToast(
          'Alerta',
          'No fue posible descargar el documento adjunto.',
          'error'
        );
      }
    },
  },
};
</script>

<style lang="scss">
@use "sass:map";
@import '@/assets/scss/variables/_colors.scss';

.appointment-readonly-panel-shell {
  position: fixed;
  inset: 0;
  z-index: 1040;
}

.appointment-readonly-panel-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(15, 30, 32, 0.42);
  backdrop-filter: blur(2px);
}

.appointment-readonly-panel {
  position: absolute;
  top: 0;
  right: 0;
  width: min(720px, 100vw);
  height: 100vh;
  background: map.get($colors, 'white');
  display: flex;
  flex-direction: column;
  box-shadow: -4px 0 18px rgba(0, 0, 0, 0.18);
}

.appointment-readonly-panel__header {
  height: 58px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  border-bottom: 1px solid map.get($colors, 'gray-3');
}

.appointment-readonly-panel__title {
  display: flex;
  align-items: center;
  gap: 8px;
  color: map.get($colors, 'gray-7');
  font-weight: 600;
}

.appointment-readonly-panel__icon,
.appointment-readonly-panel__section-icon {
  width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: map.get($colors, 'orange');
  background: map.get($colors, 'white');
  border-radius: 50%;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.14);
}

.appointment-readonly-panel__header-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.appointment-readonly-panel__icon-button {
  border: 0;
  background: transparent;
  color: map.get($colors, 'gray-6');
  padding: 4px;
  line-height: 1;
}

.appointment-readonly-panel__icon-button--danger {
  color: map.get($colors, 'red');
}

.appointment-readonly-panel__body {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px 20px;
}

.appointment-readonly-panel__tabs {
  --tab-active-color: #ff7058 !important;
  --tab-font-size: 16px;
  width: 100%;
}

.appointment-readonly-panel__tabs ::v-deep .tab-wrapper {
  width: 100%;
}

.appointment-readonly-panel__tabs
  ::v-deep
  .appointment-readonly-panel__tabs-nav {
  display: flex;
  margin: 0 0 18px;
  border-bottom: 1px solid map.get($colors, 'gray-3');
}

.appointment-readonly-panel__tabs
  ::v-deep
  .appointment-readonly-panel__tabs-nav
  .nav-item {
  flex: 1 1 0;
}

.appointment-readonly-panel__tabs
  ::v-deep
  .appointment-readonly-panel__tabs-nav
  .nav-link {
  width: 100%;
  padding: 12px;
  text-align: center;
  border-bottom: 3px solid transparent !important;
  color: map.get($colors, 'gray-6') !important;
  font-weight: 600;
}

.appointment-readonly-panel__tabs
  ::v-deep
  .custom-tab-container.appointment-readonly-panel__tabs-nav
  .nav-item
  .nav-link.active,
.appointment-readonly-panel__tabs
  ::v-deep
  .custom-tab-container.appointment-readonly-panel__tabs-nav
  .nav-link.active,
.appointment-readonly-panel__tabs
  ::v-deep
  .appointment-readonly-panel__tabs-nav
  .nav-link.active {
  border-bottom-color: #ff7058 !important;
  color: #ff7058 !important;
}

.appointment-readonly-panel__tabs ::v-deep .tab-content {
  padding-top: 12px;
}

.appointment-readonly-panel__loading,
.appointment-readonly-panel__empty-history {
  min-height: 260px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: map.get($colors, 'gray-6');
  font-weight: 600;
}

.appointment-readonly-panel__summary {
  display: flex;
  justify-content: space-between;
  background: map.get($colors, 'secondary-3');
  border-radius: 4px;
  padding: 12px 14px;
}

.appointment-readonly-panel__dependent {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  background: map.get($colors, 'secondary-3');
  border-radius: 4px;
  padding: 12px 14px;
  font-weight: 400;
}

.appointment-readonly-panel__dependent-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.appointment-readonly-panel__checks-row {
  display: flex;
  align-items: center;
  gap: 16px;
  margin: 12px 0;
}

.appointment-readonly-panel__checks-column {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.appointment-readonly-panel__check {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: map.get($colors, 'gray-7');
}

.appointment-readonly-panel__check-box {
  width: 18px;
  height: 18px;
  border: 1px solid map.get($colors, 'gray-3');
  border-radius: 3px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: map.get($colors, 'white');
  background: map.get($colors, 'white');
  font-size: 10px;
}

.appointment-readonly-panel__check-box--checked {
  background: #3d6669;
  border-color: #3d6669;
}

.appointment-readonly-panel__grid {
  display: grid;
  gap: 12px 18px;
}

.appointment-readonly-panel__grid--two {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.appointment-readonly-panel__grid--three {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.appointment-readonly-panel__official-grid {
  grid-template-columns: minmax(0, 1fr);
}

.appointment-readonly-panel__section,
.appointment-readonly-panel__field {
  margin-top: 12px;
}

.appointment-readonly-panel__section--divided {
  border-bottom: 1px solid map.get($colors, 'gray-3');
  padding-bottom: 18px;
}

.appointment-readonly-panel__numbers {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
}

.appointment-readonly-panel__label {
  display: block;
  color: map.get($colors, 'gray-6');
  font-size: 14px;
  margin-bottom: 4px;
}

.appointment-readonly-panel__number-value {
  color: #11291b;
  display: inline-block;
  font-weight: 500;
  padding: 6px 0;
}

.appointment-readonly-panel__value {
  color: map.get($colors, 'black');
  font-weight: 500;
  overflow-wrap: anywhere;
}

.appointment-readonly-panel__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.appointment-readonly-panel__chip {
  display: inline-flex;
  align-items: center;
  border: 1px solid #b5c3bc;
  border-radius: 20px;
  color: #11291b;
  background: map.get($colors, 'white');
  padding: 7px 12px;
  font-weight: 500;
}

.appointment-readonly-panel__citation-badge,
.appointment-readonly-panel__history-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border-radius: 16px;
  color: map.get($colors, 'white');
  background: map.get($colors, 'orange');
  font-weight: 600;
  padding: 6px 12px;
}

.appointment-readonly-panel__section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 18px;
  margin-bottom: 10px;
  font-weight: 700;
}

.appointment-readonly-panel__other-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 10px;
}

.appointment-readonly-panel__evaluation-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.appointment-readonly-panel__evaluation-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: map.get($colors, 'secondary-3');
  border-radius: 4px;
  padding: 10px 12px;
}

.appointment-readonly-panel__other-row,
.appointment-readonly-panel__signature-card,
.appointment-readonly-panel__history-card {
  background: map.get($colors, 'secondary-3');
  border-radius: 4px;
  padding: 12px 14px;
}

.appointment-readonly-panel__sign-plan {
  pointer-events: none;
}

.appointment-readonly-panel__signature-card {
  margin-top: 12px;
}

.appointment-readonly-panel__signature-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto;
  gap: 16px 24px;
  align-items: center;
}

.appointment-readonly-panel__signature-name {
  grid-column: 1 / -1;
}

.appointment-readonly-panel__configure-button {
  align-self: center;
  justify-self: end;
  min-width: 260px;
  white-space: nowrap;
}

.appointment-readonly-panel__download-button {
  width: 100%;
}

.appointment-readonly-panel__footer-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.appointment-readonly-panel__footer-button,
.appointment-readonly-panel__recoordinate-button {
  flex: 1 1 0;
  min-width: 0;
  width: 100%;
}

.appointment-readonly-panel__footer-button .btn,
.appointment-readonly-panel__footer-button button,
.appointment-readonly-panel__recoordinate-button .btn,
.appointment-readonly-panel__recoordinate-button button {
  width: 100%;
}

.appointment-readonly-panel__status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
}

.appointment-readonly-panel__status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.appointment-readonly-panel__history-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.appointment-readonly-panel__history-card-header {
  margin-bottom: 12px;
}

.appointment-readonly-panel__history-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px 24px;
}

.appointment-readonly-panel__history-full-row,
.appointment-readonly-panel__history-status-row {
  grid-column: 1 / -1;
}

.appointment-readonly-panel__history-status-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px 24px;
}

.appointment-readonly-panel-enter-active,
.appointment-readonly-panel-leave-active {
  transition: opacity 0.18s ease;
}

.appointment-readonly-panel-enter,
.appointment-readonly-panel-leave-to {
  opacity: 0;
}

.appointment-readonly-panel-enter .appointment-readonly-panel,
.appointment-readonly-panel-leave-to .appointment-readonly-panel {
  transform: translateX(28px);
}

.appointment-readonly-panel-enter-active .appointment-readonly-panel,
.appointment-readonly-panel-leave-active .appointment-readonly-panel {
  transition: transform 0.18s ease;
}

@media (max-width: 576px) {
  .appointment-readonly-panel {
    inset: 0;
    width: 100vw;
  }

  .appointment-readonly-panel__grid--two,
  .appointment-readonly-panel__grid--three,
  .appointment-readonly-panel__history-grid,
  .appointment-readonly-panel__history-status-row,
  .appointment-readonly-panel__signature-grid {
    grid-template-columns: 1fr;
  }

  .appointment-readonly-panel__configure-button {
    justify-self: stretch;
    width: 100%;
  }
}
</style>
